import React, { useEffect, useState } from "react";
import {
  getAccountantInvoices,
  getAccountantTransactions,
  getCashlogCategories,
} from "../common/getFunctions";
import * as labels from "../labels/en";
import commonFunctions from "../common/Functions";
import SummaryCards from "./AccountantDashboard/SummaryCards";
import FilterSection from "./AccountantDashboard/FilterSection";
import DataView from "./AccountantDashboard/DataView";
import ChartComponent from "./AccountantDashboard/ChartComponent";
import "./AccountantDashboard.css";

const AccountantDashboard = () => {
  const barColors = [
    "#368b85", "#0db66e", "#3db3e4", "#8e569f",
    "#eecd63", "#ffae94", "#dac1b9", "#e5867b",
  ];
  const lastQuarter = new Date(
    new Date().getFullYear(),
    new Date().getMonth() - 3,
    new Date().getDate()
  );
  
  // Date states
  const [fromDate, setFromDate] = useState(lastQuarter);
  const [toDate, setToDate] = useState(new Date());
  const [fromTransDate, setFromTransDate] = useState(lastQuarter);
  const [toTransDate, setToTransDate] = useState(new Date());
  
  // Display states
  const [showInvoices, setShowInvoices] = useState(false);
  const [showTransactions, setShowTransactions] = useState(false);
  
  // Data states
  const [invoices, setInvoices] = useState([]);
  const [invoicesData, setInvoicesData] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [transactionData, setTransactionData] = useState([]);
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingTrans, setLoadingTrans] = useState(false);
  
  // Filter states
  const [invoiceType, setInvoiceType] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categories, setCategories] = useState([]);
  
  // Message states
  const [resetMsg, setResetMsg] = useState("");
  const [resetTMsg, setResetTMsg] = useState("");
  
  // Currency conversion
  const lebaneseRate = 100000;
  
  // Cumulative amounts state
  const [cumulativeInvoices, setCumulativeInvoices] = useState({
    total: 0,
    paid: 0,
    pending: 0,
    currency: "USD"
  });
  const [cumulativeTransactions, setCumulativeTransactions] = useState({
    total: 0,
    income: 0,
    expense: 0,
    currency: "USD"
  });
  const [userBalance, setUserBalance] = useState({
    total: 0,
    currency: "USD"
  });

  // CSV Headers
  const invoicesHeader = [
    { label: "Number", key: "Number" },
    { label: "CLIENT NAME", key: "CLIENT NAME" },
    { label: "INVOICE DATE", key: "INVOICE DATE" },
    { label: "Start Date", key: "Start Date" },
    { label: "End Date", key: "End Date" },
    { label: "DESCRIPTION", key: "DESCRIPTION" },
    { label: "QUANTITY", key: "QUANTITY" },
    { label: "Rate", key: "Rate" },
    { label: "PRICE (EX VAT)", key: "PRICE (EX VAT)" },
    { label: "PRICE (INCL VAT)", key: "PRICE (INCL VAT)" },
    { label: "Currency", key: "CURRENCY" },
    { label: "VAT", key: "VAT" },
    { label: "Account", key: "ACCOUNT" },
    { label: "Paid", key: "Paid" },
    { label: "Completed", key: "Completed" },
    { label: "transactionNumber", key: "transactionNumber" },
    { label: "Paid At", key: "Paid At" },
    { label: "Category", key: "CATEGORY" },
    { label: "Category Key", key: "Category Key" },
    { label: "Category2", key: "CATEGORY2" },
    { label: "Category Key2", key: "Category Key2" },
    { label: "transactionAmount1", key: "transactionAmount1" },
    { label: "Original Amount", key: "Original Amount" },
    { label: "Converted Amount (USD)", key: "Converted Amount" },
    { label: "User Balance", key: "User Balance" },
    { label: "Cumulative Amount", key: "Cumulative Amount" },
  ];

  const transactionHeader = [
    { label: "Transaction Number", key: "Transaction Number" },
    { label: "Label", key: "Label" },
    { label: "Amount (EX VAT)", key: "Amount without Vat" },
    { label: "VAT", key: "VAT" },
    { label: "Amount", key: "Amount" },
    { label: "Currency", key: "Currency" },
    { label: "Original Amount", key: "Original Amount" },
    { label: "Converted Amount (USD)", key: "Converted Amount" },
    { label: "Category", key: "Category" },
    { label: "Category Key", key: "Category Key" },
    { label: "Account", key: "Account" },
    { label: "Invoice Number", key: "Invoice Number" },
    { label: "Cash Date", key: "Cash Date" },
    { label: "User Balance", key: "User Balance" },
    { label: "Cumulative Amount", key: "Cumulative Amount" },
  ];

  const currentUser = JSON.parse(localStorage.getItem("user"));
  
  useEffect(() => {
    async function onReady() {
      window.scrollTo(0, 0);
      setCategories(await getCashlogCategories(currentUser.branch));
      await triggerSearch(lastQuarter, new Date(), "");
      await triggerTransSearch(lastQuarter, new Date(), "");
    }
    setCategoryFilter("");
    onReady();
  }, []);

  useEffect(() => {
    setInvoices([]);
    setResetMsg(labels.RESET_MSG);
  }, [fromDate, toDate, invoiceType]);

  useEffect(() => {
    setTransactions([]);
    setResetTMsg(labels.RESET_MSG);
  }, [fromTransDate, toTransDate, categoryFilter]);

  const triggerSearch = async (fromDateT, toDateT, invoiceType) => {
    if (fromDateT) {
      setLoading(true);
      const invoicesRes = await getAccountantInvoices(
        fromDateT,
        toDateT,
        invoiceType,
        categoryFilter
      );
      
      let data = [];
      let result = [];

      // Calculate cumulative amounts for filtered data only
      let totalAmount = 0;
      let paidAmount = 0;
      let pendingAmount = 0;
      let runningTotal = 0;

      // Process invoices and add user balance and cumulative amounts
      const processedInvoices = invoicesRes.map((a, index) => {
        let amount = commonFunctions.removeComma(a["PRICE (INCL VAT)"]);
        let originalAmount = Number(amount) || 0;
        // Handle currency conversion to USD for other fields if needed
        let convertedAmount = originalAmount;
        if (a.CURRENCY === "LBP") {
          convertedAmount = originalAmount / lebaneseRate;
        } else if (a.CURRENCY === "EUR" && a.Rate && a.Rate !== "Not set") {
          convertedAmount = originalAmount * parseFloat(a.Rate);
        } else if (a.CURRENCY === "USD") {
          convertedAmount = originalAmount;
        }
        // No calculation for User Balance or Cumulative Amount
        return {
          ...a,
          "Original Amount": commonFunctions.CommaFormatted(originalAmount.toFixed(2)) + " " + a.CURRENCY,
          "Converted Amount": commonFunctions.CommaFormatted(convertedAmount.toFixed(2)) + " USD"
        };
      });

      setInvoices(processedInvoices);
      setResetMsg("");

      // Group by account and currency for pie chart (using original currency)
      processedInvoices.forEach(function (a) {
        const name = a.ACCOUNT + "-" + a.CURRENCY;
        let amount = commonFunctions.removeComma(a["PRICE (INCL VAT)"]);
        amount = Number(amount) || 0;
        // No conversion to USD
        if (a.ACCOUNT) {
          if (!this[name]) {
            this[name] = { name: name, amount: 0, currency: a.CURRENCY };
            result.push(this[name]);
          }
          this[name].amount += Number(amount);
        }
      }, Object.create(null));

      // Update cumulative invoices state (filtered data only, all in USD)
      setCumulativeInvoices({
        total: totalAmount,
        paid: paidAmount,
        pending: pendingAmount,
        currency: "USD"
      });

      result &&
        result.map((s, i) => {
          data.push({
            name: s.name,
            value: s.amount,
            currency: s.currency,
          });
        });

      setInvoicesData(data);
      setLoading(false);
    }
  };

  const triggerTransSearch = async (fromDateT, toDateT, categoryFilter) => {
    if (fromDateT) {
      setLoadingTrans(true);
      const transactionRes = await getAccountantTransactions(
        fromDateT,
        toDateT,
        categoryFilter
      );
      let data2 = [];
      let result2 = [];

      // Calculate cumulative amounts for filtered data only
      let totalAmount = 0;
      let incomeAmount = 0;
      let expenseAmount = 0;
      let runningTotal = 0;

      // Process transactions and add user balance and cumulative amounts
      const processedTransactions = transactionRes.map((a, index) => {
        let amount = commonFunctions.removeComma(a.Amount);
        let originalAmount = Number(amount) || 0;
        // Handle currency conversion to USD for other fields if needed
        let convertedAmount = originalAmount;
        if (a.Currency === "LBP") {
          convertedAmount = originalAmount / lebaneseRate;
        } else if (a.Currency === "EUR") {
          convertedAmount = originalAmount;
        }
        // No calculation for User Balance or Cumulative Amount
        return {
          ...a,
          "Original Amount": commonFunctions.CommaFormatted(originalAmount.toFixed(2)) + " " + a.Currency,
          "Converted Amount": commonFunctions.CommaFormatted(convertedAmount.toFixed(2)) + " USD"
        };
      });

      setTransactions(processedTransactions);

      // Group by account and currency for pie chart (using original currency)
      processedTransactions.forEach(function (a) {
        const name = a.Account + "-" + a.Currency;
        let amount = commonFunctions.removeComma(a.Amount);
        amount = Number(amount) || 0;
        // No conversion to USD
        if (a.Account) {
          if (!this[name]) {
            this[name] = { name: name, amount: 0, currency: a.Currency };
            result2.push(this[name]);
          }
          this[name].amount += Math.abs(Number(amount));
        }
      }, Object.create(null));

      // Update cumulative transactions state (filtered data only)
      setCumulativeTransactions({
        total: totalAmount,
        income: incomeAmount,
        expense: expenseAmount,
        currency: "USD"
      });

      // Calculate user balance (income - expenses)
      const balance = incomeAmount - expenseAmount;
      setUserBalance({
        total: balance,
        currency: "USD"
      });

      result2 &&
        result2.map((s, i) => {
          data2.push({
            name: s.name,
            value: s.amount,
            currency: s.currency,
          });
        });
      setResetTMsg("");
      setTransactionData(data2);
      setLoadingTrans(false);
    }
  };

  const resetSearch = async () => {
    setFromDate(lastQuarter);
    setToDate(new Date());
    await triggerSearch(lastQuarter, new Date(), invoiceType);
  };

  const resetTransactionSearch = async () => {
    setFromTransDate(lastQuarter);
    setToTransDate(new Date());
    await triggerTransSearch(lastQuarter, new Date(), categoryFilter);
  };

  const handleTypeChange = async (type) => {
    setInvoiceType(type);
  };
  
  const handleCategoryChange = async (cat) => {
    setCategoryFilter(cat);
  };

  // Chart components
  const invoicesChart = (
    <ChartComponent 
      data={invoicesData} 
      type="invoices" 
      barColors={barColors} 
    />
  );

  const transactionsChart = (
    <ChartComponent 
      data={transactionData} 
      type="transactions" 
      barColors={barColors} 
    />
  );

  return (
    <div id="accountant-dashboard-root"><div className="accountant-dashboard">
      {/* Invoices Section */}
      <div className="dashboard-section">
        <h3>Invoices List</h3>
        <FilterSection 
          type="invoices"
          fromDate={fromDate}
          toDate={toDate}
          setFromDate={setFromDate}
          setToDate={setToDate}
          invoiceType={invoiceType}
          setInvoiceType={setInvoiceType}
          onSearch={() => triggerSearch(fromDate, toDate, invoiceType)}
          onReset={resetSearch}
        />
        
        {resetMsg !== "" ? (
          <div className="defaultMsg">{resetMsg}</div>
        ) : (
          <>
            <DataView 
              data={invoices}
              type="invoices"
              headers={invoicesHeader}
              loading={loading}
              showData={showInvoices}
              setShowData={setShowInvoices}
              fromDate={fromDate}
              toDate={toDate}
              chartComponent={invoicesChart}
            />
          </>
        )}
      </div>

      {/* Transactions Section */}
      <div className="dashboard-section">
        <h3>Transactions List</h3>
        <FilterSection 
          type="transactions"
          fromDate={fromTransDate}
          toDate={toTransDate}
          setFromDate={setFromTransDate}
          setToDate={setToTransDate}
          categoryFilter={categoryFilter}
          setCategoryFilter={setCategoryFilter}
          categories={categories}
          onSearch={() => triggerTransSearch(fromTransDate, toTransDate, categoryFilter)}
          onReset={resetTransactionSearch}
        />
        
        {resetTMsg !== "" ? (
          <div className="defaultMsg">{resetTMsg}</div>
        ) : (
          <>
            <DataView 
              data={transactions}
              type="transactions"
              headers={transactionHeader}
              loading={loadingTrans}
              showData={showTransactions}
              setShowData={setShowTransactions}
              fromDate={fromTransDate}
              toDate={toTransDate}
              chartComponent={transactionsChart}
            />
          </>
        )}
      </div>
    </div></div>
  );
};

export default AccountantDashboard; 